/*
 * The drive's event union: the only place that pairs a `drive.*` type string with its payload
 * (`internal/modules/drive/wire/types.go`). It is hand-written because tygo emits the type strings as
 * constants and the payloads as interfaces but cannot join the two; it is what makes `reducer.ts`
 * exhaustive, and `fixtures.test.ts` holds it to the fixtures the Go side writes for every type.
 *
 * All six are **durable**: kept in the change log and replayed to a reader who was away. They carry a
 * space's or an item's view, the id of what left, or a batch's key and state — never a word of a
 * document, which a page reads through core's documents. The drive has no socket request: every act is an HTTP route (`api.ts`).
 */

import type { DurableEvent } from "../../core/wire/events.ts"
import type {
	BatchChanged,
	ItemLeft,
	ItemView,
	SpaceLeft,
	SpaceView,
	StarChanged,
} from "./wire/drive.gen.ts"

export type DriveEvent =
	// A space's whole view whenever it was made, renamed, archived, restored or its groups changed.
	| DurableEvent<"drive.space.changed", SpaceView>
	// To one person: a space they no longer reach.
	| DurableEvent<"drive.space.left", SpaceLeft>
	// An item's whole view, on its space, whenever anything a listing shows of it moved.
	| DurableEvent<"drive.item.changed", ItemView>
	// On an item's old space, when it moved to another.
	| DurableEvent<"drive.item.left", ItemLeft>
	// To the person who starred or unstarred.
	| DurableEvent<"drive.star.changed", StarChanged>
	// On a space, when an AI chat's batch of changes there opened, grew, was decided, withdrawn or
	// undone: a page that lists the drive's batches reads them again.
	| DurableEvent<"drive.batch.changed", BatchChanged>

declare module "../../core/wire/index.ts" {
	interface ModuleEvents {
		drive: DriveEvent
	}
}

/** One of the drive's six types. */
export type DriveEventType = DriveEvent["type"]

/** Every type string the union carries, in the order of the Go list (`DriveEventTypes`). */
export const DRIVE_EVENT_TYPES = [
	"drive.space.changed",
	"drive.space.left",
	"drive.item.changed",
	"drive.item.left",
	"drive.star.changed",
	"drive.batch.changed",
] as const satisfies readonly DriveEventType[]

const KNOWN = new Set<string>(DRIVE_EVENT_TYPES)

/** Whether a wire type is one this module owns, which is what the reducer answers by. */
export function isDriveEventType(type: string): type is DriveEventType {
	return KNOWN.has(type)
}
